import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparent?: boolean;
}

interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
  emphasis?: 'outline' | 'solid';
}

const navItems: NavItem[] = [
  { label: '首页', path: '/' },
  { label: '关于我们', path: '/about' },
  {
    label: '核心业务',
    path: '/business',
    children: [
      { label: '作品案例', path: '/case' },
      { label: '技术实力', path: '/technology' },
      { label: '文化出海', path: '/overseas' },
      { label: '产教融合', path: '/education' },
    ],
  },
  { label: '新闻动态', path: '/news' },
  { label: '招商合作', path: '/cooperation', emphasis: 'outline' },
  { label: '联系我们', path: '/contact', emphasis: 'solid' },
];

/** 判断某个主项是否处于激活态（含子路由） */
function isActiveItem(item: NavItem, pathname: string): boolean {
  return pathname === item.path || item.children?.some((c) => c.path === pathname) === true;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  // --- 下划线指示器：统一用 flex 容器做参照，避免被包裹层干扰 ---
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  // 桌面端下拉触发区 ref（用于判定点击是否在下拉外部）
  const dropdownWrapperRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const updateIndicator = useCallback(() => {
    const activeIndex = navItems.findIndex((item) => isActiveItem(item, location.pathname));
    if (activeIndex === -1) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }
    const el = navItemRefs.current[activeIndex];
    const container = navContainerRef.current;
    if (!el || !container) {
      // React ref cleanup 期间元素可能短暂缺失，下一帧再试一次
      requestAnimationFrame(() => updateIndicator());
      return;
    }
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setIndicatorStyle({
      left: elRect.left - containerRect.left,
      width: elRect.width,
      opacity: 1,
    });
  }, [location.pathname]);

  useLayoutEffect(() => {
    updateIndicator();
    const handleResize = () => updateIndicator();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateIndicator, location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // 桌面端：点击/触摸下拉外部区域或按 Esc 时，真正关闭下拉
  useEffect(() => {
    if (!openDropdown || isMobileMenuOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      const wrapper = dropdownWrapperRefs.current[openDropdown];
      if (wrapper && !wrapper.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenDropdown(null);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDropdown, isMobileMenuOpen]);

  const bgClass = !transparent || isScrolled || !isHome
    ? 'bg-background-950/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(212,175,55,0.1)]'
    : 'bg-transparent';

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${bgClass}`}>
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="group shrink-0">
            <img
              src="/logo.png"
              alt="晋铧叙梦（山西）文化科技有限公司"
              className="h-11 md:h-14 w-auto object-contain transition-opacity duration-300 group-hover:opacity-90"
            />
          </Link>

          {/* 桌面导航 + 滑动下划线 */}
          <div ref={navContainerRef} className="hidden lg:flex items-center gap-1 xl:gap-3 relative">
            {navItems.map((item, index) => {
              const isActive = isActiveItem(item, location.pathname);
              const hasChildren = !!item.children;

              if (hasChildren) {
                const isOpen = openDropdown === item.path;
                return (
                  <div
                    key={item.path}
                    ref={(el) => { dropdownWrapperRefs.current[item.path] = el; }}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.path)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <Link
                      to={item.path}
                      ref={(el) => { navItemRefs.current[index] = el; }}
                      className={`relative px-3 xl:px-5 py-2 text-sm whitespace-nowrap transition-colors duration-300 rounded-md cursor-pointer flex items-center gap-1 ${
                        isActive
                          ? 'text-primary-500'
                          : 'text-secondary-400 hover:text-foreground-100'
                      }`}
                    >
                      {item.label}
                      <i
                        className={`text-xs transition-transform duration-300 ${
                          isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                        } ${isActive ? 'text-primary-500' : ''}`}
                      />
                    </Link>
                    {/* 下拉菜单：条件渲染，关闭时彻底从 DOM 卸载，不可能残留 */}
                    {isOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                        <div className="nav-dropdown-enter bg-background-900/95 backdrop-blur-xl border border-primary-500/15 rounded-xl overflow-hidden shadow-lg min-w-[160px]">
                          {item.children!.map((child, childIndex) => {
                            const childActive = location.pathname === child.path;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`stagger-item block px-4 py-2.5 text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                                  childActive
                                    ? 'text-primary-500 bg-primary-500/10 font-medium'
                                    : 'text-secondary-300 hover:text-foreground-100 hover:bg-background-800/60'
                                }`}
                                style={{ animationDelay: `${childIndex * 25}ms` }}
                              >
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  ref={(el) => { navItemRefs.current[index] = el; }}
                  className={`relative px-3 xl:px-5 py-2 text-sm whitespace-nowrap transition-all duration-300 rounded-md cursor-pointer ${
                    item.emphasis === 'solid'
                      ? 'bg-primary-500 text-background-950 hover:bg-primary-400 font-medium rounded-full'
                      : item.emphasis === 'outline'
                        ? `border rounded-full font-medium ${isActive ? 'border-primary-500 text-primary-500 bg-primary-500/10' : 'border-primary-500/55 text-primary-500 hover:bg-primary-500/10'}`
                        : isActive
                          ? 'text-primary-500'
                          : 'text-secondary-400 hover:text-foreground-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            {/* 滑动下划线指示器 */}
            <span
              className="absolute bottom-0 h-0.5 bg-primary-500 rounded-full pointer-events-none transition-all duration-400 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
              style={{
                left: indicatorStyle.left,
                width: indicatorStyle.width,
                opacity: indicatorStyle.opacity,
              }}
            />
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-secondary-300 hover:text-foreground-50 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-background-950/98 backdrop-blur-xl border-t border-primary-500/10">
          <div className="px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            {navItems.map((item, index) => {
              const isActive = isActiveItem(item, location.pathname);
              const hasChildren = !!item.children;

              if (hasChildren) {
                const isOpen = openDropdown === item.path;
                return (
                  <div key={item.path} className="stagger-item" style={{ animationDelay: `${index * 60}ms` }}>
                    <button
                      onClick={() => setOpenDropdown(isOpen ? null : item.path)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        isActive
                          ? 'bg-primary-500/10 text-primary-500 font-medium'
                          : 'text-secondary-400 hover:text-foreground-50 hover:bg-background-800/50'
                      }`}
                    >
                      <span>{item.label}</span>
                      <i
                        className={`text-base transition-transform duration-300 ${
                          isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                        }`}
                      />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="pl-4 flex flex-col gap-0.5 mt-1">
                        {item.children!.map((child, childIndex) => {
                          const childActive = location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`stagger-item px-4 py-2.5 rounded-lg text-sm whitespace-nowrap transition-all duration-200 cursor-pointer ${
                                childActive
                                  ? 'text-primary-500 bg-primary-500/10 font-medium'
                                  : 'text-secondary-500 hover:text-foreground-100 hover:bg-background-800/40'
                              }`}
                              style={{ animationDelay: `${childIndex * 50}ms` }}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`stagger-item px-4 py-3 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                    item.emphasis === 'solid'
                      ? 'bg-primary-500 text-background-950 font-medium'
                      : item.emphasis === 'outline'
                        ? `border font-medium ${isActive ? 'border-primary-500 bg-primary-500/10 text-primary-500' : 'border-primary-500/55 text-primary-500 hover:bg-primary-500/10'}`
                        : isActive
                          ? 'bg-primary-500/10 text-primary-500 font-medium'
                          : 'text-secondary-400 hover:text-foreground-50 hover:bg-background-800/50'
                  }`}
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}
